<template>
  <div class="stylish-safe-wrapper">
    <TransitionGroup
      name="fade"
      mode="out-in">
      <div
        key="stylish-safe"
        class="row mb-xxl gap-lg">
        <div class="offset-1 offset-sm-2 offset-md-2 offset-lg-1 offset-xl-2 col-10 col-sm-8 col-lg-5 col-xl-4 d-flex flex-column justify-content-center gap-lg order-2 order-lg-1">
          <h2>
            Keeping you stylish + safe
          </h2>
          <p>At Armoire, we have tight processes to ensure all clothing is cleaned and sanitized prior to shipping. We use both wet and dry methods for cleaning, and utilize a steam process which heats to 250 degrees. (According to the CDC, flu viruses are killed by heating to 167 degrees or higher). </p>
          <GetStartedButton
            location="Stylish Safe"
            :class="isMobile ? 'w-100' : 'fixed-width'"/>
        </div>
        <div class="offset-1 offset-sm-2 offset-lg-0 col-10 col-sm-8 col-lg-5 col-xl-4 order-1 order-lg-2">
          <homepage-image
            width="100%"
            height="auto"
            alt="Armoire employee holding a dress in the Armoire warehouse"
            webp-source="howitworks/jan2022/stylishsafe.webp"
            source="howitworks/jan2022/stylishsafe.jpg"
            source-type="image/jpeg"/>
        </div>
      </div>
      <div
        key="stylish-bestie"
        class="row">
        <div class="offset-1 offset-sm-2 offset-md-2 offset-lg-1 offset-xl-2 col-10 col-sm-8 col-lg-5 col-xl-4 bg-origami h-100">
          <Transition
            name="fade"
            mode="out-in"
            appear>
            <div
              :key="selectedMxTeamMember.name"
              class="mx-member-wrapper d-flex flex-column justify-content-center align-items-center justify-content-between py-lgp">
              <h3 class="mx-member-name text-velvet">
                {{ selectedMxTeamMember.name }}
              </h3>
              <div class="d-flex align-items-center justify-content-between pt-xl pb-md">
                <plain-button
                  class="p-md pe-xl"
                  @click="updateIndex(-1)">
                  <svg-chevron-left/>
                </plain-button>
        
                <homepage-image
                  width="100%"
                  height="auto"
                  class="pb-lgp"
                  :alt="`Profile picture of ${selectedMxTeamMember.name}`"
                  :webp-source="`howitworks/jan2022/stylebestie/${selectedMxTeamMember.id}.webp`"
                  :source="`howitworks/jan2022/stylebestie/${selectedMxTeamMember.id}.png`"
                  source-type="image/png"/>
                <plain-button
                  class="p-md ps-xl"
                  @click="updateIndex(1)">
                  <svg-chevron-right/>
                </plain-button>
              </div>
              <p class="mx-member-quote quote mx-md">
                “{{ selectedMxTeamMember.quote }}”
              </p>
            </div>
          </Transition>
        </div>
        <div class="offset-1 offset-sm-2 offset-lg-0 col-10 col-sm-8 col-lg-5 col-xl-4 d-flex flex-column justify-content-center gap-lg ps-lg-xxl pt-lgp pt-lg-0">
          <h2>
            Meet your new style bestie
          </h2>
          <p>
            Request help from the experts at your convenience! Ask a stylist to pull items for a special occasion or to fit a unique need by using the chat feature on our website or by emailing hi@armoire.style. Short on time? Stylists can also select items for you with the click of a button! 
          </p>
          <GetStartedButton
            :class="isMobile ? 'w-100' : 'fixed-width'"
            location="Stylish Bestie"/>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import useScreenSize from '@shared/composables/screenSize'
import HomepageImage from '@/homepage/components/HomepageImage.vue'
import GetStartedButton from '@/homepage/components/GetStartedButton.vue'
import PlainButton from '@/components/global/sequin/PlainButton.vue' // TODO: ENG-3647 - Isolate shared code.
import SvgChevronLeft from '@/components/global/svg/SvgChevronLeft.vue'
import SvgChevronRight from '@/components/global/svg/SvgChevronRight.vue'

const { isMobile } = useScreenSize()
const mxTeamIndex = ref(0)
const mxTeamMembers = ref([
  {
    id: 'kaylee',
    name: 'Kaylee',
    quote: 'Styling has been a passion of mine for as long as I can remember. Everyone is unique, and I love helping people express themselves through the way they dress'
  },
  {
    id: 'jayla',
    name: 'Jayla',
    quote: 'Style is a form of self expression. Let\'s elevate your current wardrobe with key pieces to help you look and feel your best with confidence!'
  },
  {
    id: 'nicolette',
    name: 'Nicolette',
    quote: 'Whether you have special events to attend or you\'re working from home, feeling good in what you wear is important. Let\'s get these outfits started!'
  }
])
const selectedMxTeamMember = computed(() => {
  return mxTeamMembers.value[mxTeamIndex.value]
})
const updateIndex = (increment) => {
  mxTeamIndex.value += increment
  if (mxTeamIndex.value === -1) mxTeamIndex.value = mxTeamMembers.value.length - 1
  if (mxTeamIndex.value === mxTeamMembers.value.length) mxTeamIndex.value = 0
}
</script>

<style lang="scss" scoped>
.stylish-safe-wrapper {
  max-width: 1440px;
  margin: 0 auto;
}
.mx-member-wrapper{
  width: 100%;
  height: fit-content;
}
.mx-member-name {
  margin-bottom: -32px;
  text-transform: uppercase;
  font-family: Open Sans, Helvetica, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  letter-spacing: 3.2px;
  line-height: 24px
}
</style>
